/**
 * /crew — supervisor's crew roster screen.
 *
 * One card per active crew member with role, truck, capacity, and a load bar
 * for the current 7-day window. Supervisors (and admins) can edit a member's
 * crew role / truck / capacity inline and add a user to the roster
 * (RoleMgmt.1). Click a card's arrow to jump to the dispatch board.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { addDays, addWeeks, format, parseISO, startOfWeek } from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  User,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import type { CrewRole, CrewUtilizationRow } from '@vari-crew/schemas';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ConfirmDialog } from '@/components/ui/Dialog';
import {
  useCreateCrewMember,
  useCrewCandidates,
  useCrewUtilization,
  useRemoveCrewMember,
  useUpdateCrewMember,
} from '@/api/crew';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

const CREW_ROLES: CrewRole[] = ['lead', 'installer', 'driver', 'trainee'];
const selectCls =
  'h-8 rounded border border-line bg-bg px-2 text-sm capitalize';

function weekStartOf(d: Date): Date {
  return startOfWeek(d, { weekStartsOn: 1 });
}

export function CrewRoster() {
  const [weekAnchor, setWeekAnchor] = useState<Date>(() =>
    weekStartOf(new Date()),
  );
  const [adding, setAdding] = useState(false);
  const { from, to } = useMemo(() => {
    const start = weekStartOf(weekAnchor);
    return {
      from: format(start, 'yyyy-MM-dd'),
      to: format(addDays(start, 6), 'yyyy-MM-dd'),
    };
  }, [weekAnchor]);
  const { data, isLoading, error } = useCrewUtilization(from, to);
  const navigate = useNavigate();
  const isCurrentWeek = format(weekStartOf(new Date()), 'yyyy-MM-dd') === from;

  if (isLoading) {
    return (
      <div className="p-6 grid place-items-center">
        <Loader2 className="w-5 h-5 animate-spin text-ink-faint" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6 text-status-blocked-fg text-sm">
        {(error as Error).message}
      </div>
    );
  }
  if (!data) return null;

  const sorted = [...data.rows].sort((a, b) => {
    const aRatio = a.capacity_hours ? a.assigned_hours / a.capacity_hours : 0;
    const bRatio = b.capacity_hours ? b.assigned_hours / b.capacity_hours : 0;
    return bRatio - aRatio;
  });

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Crew &amp; capacity</h1>
          <p className="text-sm text-ink-dim">
            {sorted.length} active{' '}
            {sorted.length === 1 ? 'crew member' : 'crew members'} · week of{' '}
            {format(parseISO(from), 'MMM d')} –{' '}
            {format(parseISO(to), 'MMM d, yyyy')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setAdding((v) => !v)} size="sm">
            <Plus className="w-4 h-4" />
            Add crew member
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setWeekAnchor(addWeeks(weekAnchor, -1))}
            aria-label="Previous week"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setWeekAnchor(addWeeks(weekAnchor, 1))}
            aria-label="Next week"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          {!isCurrentWeek && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setWeekAnchor(weekStartOf(new Date()))}
            >
              This week
            </Button>
          )}
        </div>
      </header>

      {adding && <AddCrewMemberPanel onClose={() => setAdding(false)} />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {sorted.map((row) => (
          <CrewCard
            key={row.crew_member.id}
            row={row}
            onJump={() => {
              if (row.next_task_scheduled_start) {
                const date = format(
                  parseISO(row.next_task_scheduled_start),
                  'yyyy-MM-dd',
                );
                navigate(`/dispatch?date=${date}`);
              } else {
                navigate('/dispatch');
              }
            }}
          />
        ))}
      </div>
    </div>
  );
}

function AddCrewMemberPanel({ onClose }: { onClose: () => void }) {
  const { data, isLoading } = useCrewCandidates();
  const create = useCreateCrewMember();
  const [userId, setUserId] = useState('');
  const [crewRole, setCrewRole] = useState<CrewRole>('installer');
  const [capacity, setCapacity] = useState(8);
  const candidates = data?.data ?? [];

  async function submit() {
    if (!userId) {
      toast.error('Pick a crew user to add.');
      return;
    }
    try {
      const created = await create.mutateAsync({
        user_id: userId,
        crew_role: crewRole,
        // Trucks are booked per task now (ADR-0019), not owned by a person.
        truck_label: null,
        vehicle_plate: null,
        default_capacity_hours: capacity,
      });
      toast.success(`Added ${created.user.display_name} to the crew`);
      onClose();
    } catch (err) {
      toast.error(`Failed to add: ${(err as Error).message}`);
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm">Add crew member</CardTitle>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
          <X className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="text-sm text-ink-faint">Loading eligible users…</div>
        ) : candidates.length === 0 ? (
          <div className="text-sm text-ink-dim">
            No eligible users. A person must have the <strong>crew</strong> or{' '}
            <strong>supervisor</strong> role (set by an admin) and not already be
            on the roster — a supervisor can be added as a player-coach.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="text-xs text-ink-faint">Crew user</span>
              <select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="mt-1 w-full h-8 rounded border border-line bg-bg px-2 text-sm"
              >
                <option value="">Select a user…</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.display_name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="text-xs text-ink-faint">Crew role</span>
              <select
                value={crewRole}
                onChange={(e) => setCrewRole(e.target.value as CrewRole)}
                className={cn('mt-1 w-full', selectCls)}
              >
                {CREW_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm block">
              <span className="text-xs text-ink-faint">Hours per day</span>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  max={24}
                  value={capacity}
                  onChange={(e) => setCapacity(Number(e.target.value))}
                  className="h-8 w-20"
                  aria-label="Hours per day"
                />
                <span className="text-sm text-ink-dim">hrs/day</span>
              </div>
            </label>
          </div>
        )}
        {candidates.length > 0 && (
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" onClick={submit} disabled={create.isPending}>
              {create.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Add to crew
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CrewCard({
  row,
  onJump,
}: {
  row: CrewUtilizationRow;
  onJump: () => void;
}) {
  const m = row.crew_member;
  const update = useUpdateCrewMember();
  const remove = useRemoveCrewMember();
  const [editing, setEditing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [crewRole, setCrewRole] = useState<CrewRole>(m.crew_role);
  const [capacity, setCapacity] = useState(m.default_capacity_hours);

  const ratio =
    row.capacity_hours > 0 ? row.assigned_hours / row.capacity_hours : 0;
  const pct = Math.round(ratio * 100);
  const isOver = ratio > 1;
  const isUnder = ratio < 0.4;
  // Plain-English weekly state + the labeled hours that drive it.
  const overBy = Math.round((row.assigned_hours - row.capacity_hours) * 10) / 10;
  const freeBy = Math.round((row.capacity_hours - row.assigned_hours) * 10) / 10;
  const weekdays =
    m.default_capacity_hours > 0
      ? Math.round(row.capacity_hours / m.default_capacity_hours)
      : 0;
  const week = isOver
    ? { label: 'Overbooked', cls: 'text-status-blocked-fg border-status-blocked bg-status-blocked/15' }
    : ratio > 0.8
      ? { label: 'Nearly full', cls: 'text-status-enroute-fg border-status-enroute bg-status-enroute/15' }
      : isUnder
        ? { label: 'Light week', cls: 'text-ink-faint border-line' }
        : { label: 'On track', cls: 'text-status-complete-fg border-status-complete bg-status-complete/15' };
  const initials = m.user.display_name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  function startEdit() {
    setCrewRole(m.crew_role);
    setCapacity(m.default_capacity_hours);
    setEditing(true);
  }

  async function save() {
    try {
      await update.mutateAsync({
        id: m.id,
        body: {
          crew_role: crewRole,
          default_capacity_hours: capacity,
        },
      });
      toast.success(`Updated ${m.user.display_name}`);
      setEditing(false);
    } catch (err) {
      toast.error(`Failed to update: ${(err as Error).message}`);
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-fill-soft flex items-center justify-center text-xs font-bold flex-shrink-0">
              {initials}
            </div>
            <div className="min-w-0">
              <CardTitle className="truncate">{m.user.display_name}</CardTitle>
              {/* Role + daily capacity on the card face, so the weekly hours
                  below read as "Nh/day × weekdays". */}
              <div className="flex items-center gap-1.5 text-xs text-ink-dim mt-0.5 flex-wrap">
                <User className="w-3 h-3" />
                <span className="capitalize">{m.crew_role}</span>
                <span className="text-ink-faint">·</span>
                <span>{m.default_capacity_hours}h/day</span>
              </div>
            </div>
          </div>
          {!editing && (
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={startEdit}
                className="text-ink-faint hover:text-ink"
                title="Edit role and hours per day"
                aria-label="Edit crew member"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setConfirmRemove(true)}
                className="text-ink-faint hover:text-status-blocked-fg"
                title="Remove from crew roster"
                aria-label={`Remove ${m.user.display_name} from the crew`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={onJump}
                className="text-ink-faint hover:text-ink"
                title="Open on dispatch board"
                aria-label="Open on dispatch board"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </CardHeader>
      <ConfirmDialog
        open={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={() =>
          remove.mutate(m.id, {
            onSuccess: () => {
              setConfirmRemove(false);
              toast.success(`Removed ${m.user.display_name} from the crew`);
            },
            onError: (e) => toast.error(`Failed to remove: ${e.message}`),
          })
        }
        title="Remove from crew roster?"
        body={`${m.user.display_name} will drop off the dispatch board, schedule, and assign picker. Their past work stays in the records, and you can add them back anytime.`}
        confirmLabel="Remove"
        tone="danger"
        busy={remove.isPending}
      />
      <CardContent className="pt-0">
        {editing ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-medium text-ink">Crew role</span>
                <select
                  value={crewRole}
                  onChange={(e) => setCrewRole(e.target.value as CrewRole)}
                  className="mt-1 w-full h-9 rounded border border-line bg-bg px-2 text-sm capitalize"
                >
                  {CREW_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-ink">Hours per day</span>
                <div className="mt-1 flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    max={24}
                    value={capacity}
                    onChange={(e) => setCapacity(Number(e.target.value))}
                    className="h-9 w-20"
                    aria-label="Hours per day"
                  />
                  <span className="text-sm text-ink-dim">hrs/day</span>
                </div>
              </label>
            </div>
            <p className="text-xs text-ink-faint">
              Daily capacity for this person — multiplied across the weekdays they
              work to set the weekly hours shown below.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(false)}
                disabled={update.isPending}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={save} disabled={update.isPending}>
                {update.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Save changes
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-ink-faint uppercase tracking-wide">
                Hours this week
              </span>
              <span
                className={cn(
                  'text-[11px] px-1.5 py-0.5 rounded-full border font-medium',
                  week.cls,
                )}
              >
                {week.label}
              </span>
            </div>
            <div className="h-2 bg-fill-soft rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full transition-all',
                  isOver
                    ? 'bg-status-blocked-fg'
                    : ratio > 0.8
                      ? 'bg-status-enroute-fg'
                      : 'bg-status-complete-fg',
                )}
                style={{ width: `${Math.min(100, Math.max(2, pct))}%` }}
              />
            </div>
            {/* Spell out the two numbers in words — "60.5h / 40h · 151%" was the
                unintuitive part. */}
            <div className="mt-1.5 text-xs text-ink-dim">
              <span className="font-semibold text-ink">{row.assigned_hours}h</span> booked
              of{' '}
              <span
                className="font-semibold text-ink"
                title={weekdays > 0 ? `${m.default_capacity_hours}h/day × ${weekdays} weekdays` : undefined}
              >
                {row.capacity_hours}h
              </span>{' '}
              capacity
              {isOver ? (
                <span className="text-status-blocked-fg font-medium"> · {overBy}h over</span>
              ) : freeBy > 0 ? (
                <span className="text-ink-faint"> · {freeBy}h free</span>
              ) : null}
            </div>

            <div className="mt-3 text-xs">
              {row.next_task_id && row.next_task_title ? (
                <div className="text-ink-dim">
                  <span className="text-ink-faint uppercase tracking-wide">
                    Next:{' '}
                  </span>
                  <Link
                    to={`/projects/`}
                    onClick={(e) => {
                      e.preventDefault();
                      onJump();
                    }}
                    className="hover:underline text-ink"
                  >
                    {row.next_task_title}
                  </Link>
                  {row.next_task_scheduled_start && (
                    <span className="text-ink-faint">
                      {' · '}
                      {format(
                        parseISO(row.next_task_scheduled_start),
                        'EEE MMM d · h:mma',
                      ).toLowerCase()}
                    </span>
                  )}
                </div>
              ) : (
                <div className="text-ink-faint italic">
                  No upcoming assignments this week
                </div>
              )}
              <div className="text-ink-faint mt-1">
                {row.task_count} task{row.task_count === 1 ? '' : 's'} scheduled
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
